import React, { useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { Pencil, Trash2 } from "lucide-react";
import { fetchJSON } from "@/lib/api";

type TankOption = {
  id: string;
  tank_number?: string;
  tank_name?: string;
  tankNumber?: string;
  product_name?: string;
  current_stock?: number;
  currentStock?: number;
};

type NozzleReading = {
  id: string;
  label: string;
  open: string;
  close: string;
};

type TankerRow = {
  _id: string;
  id?: string;
  date?: string;
  endDate?: string;
  invoice?: string;
  vehicle?: string;
  mobile?: string;
  tank?: string;
  beforeDip?: number;
  beforeStock?: number;
  receipt?: number;
  gross?: number;
  totalSale?: number;
};

function getTankLabel(tank: TankOption): string {
  const raw = tank.tank_name || tank.tank_number || tank.tankNumber || "";
  if (!raw) return `TANK-${tank.id.slice(-4).toUpperCase()}`;
  const upper = raw.toUpperCase();
  if (upper.startsWith("TANK")) return upper;
  return `TANK-${raw}`;
}

function tankMatchesValue(tank: TankOption, value: string): boolean {
  if (!value) return false;
  return (
    tank.id === value ||
    tank.tank_number === value ||
    tank.tank_name === value ||
    tank.tankNumber === value ||
    getTankLabel(tank) === value ||
    value.toUpperCase() === getTankLabel(tank).toUpperCase()
  );
}

function resolveTankLabel(tanks: TankOption[], value: string): string {
  if (!value) return "—";
  const found = tanks.find((t) => tankMatchesValue(t, value));
  return found ? getTankLabel(found) : value;
}

function resolveTankId(tanks: TankOption[], tankValue: string): string {
  if (!tankValue) return "";
  const found = tanks.find((t) => tankMatchesValue(t, tankValue));
  return found?.id || tankValue;
}

function formatDumpDate(row: TankerRow): string {
  const raw = row.date || row.endDate;
  if (!raw) return "—";
  try {
    return format(new Date(raw), "dd/MM/yyyy");
  } catch {
    return "—";
  }
}

function toDateInputValue(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function normalizeTankerRow(raw: Record<string, unknown>): TankerRow {
  return {
    ...(raw as TankerRow),
    _id: String(raw._id ?? raw.id ?? ""),
  };
}

function getRowId(row: TankerRow): string {
  return String(row._id || row.id || "").trim();
}

function rowClosingBalance(row: TankerRow): number {
  const gross = Number(row.gross) || 0;
  const sale = Number(row.totalSale) || 0;
  if (gross > 0 || sale > 0) return gross - sale;
  return Number(row.beforeDip) || 0;
}

const SortIcon = () => (
  <div className="flex flex-col">
    <svg className="h-3 w-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
    </svg>
    <svg className="h-3 w-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  </div>
);

export default function TankerSaleSimple() {
  const [rows, setRows] = useState<TankerRow[]>([]);
  const [tanks, setTanks] = useState<TankOption[]>([]);
  const [activeTab, setActiveTab] = useState("");
  const [search, setSearch] = useState("");
  const [entries, setEntries] = useState("All");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const formSectionRef = useRef<HTMLDivElement>(null);
  const pendingEditRowRef = useRef<TankerRow | null>(null);
  const suppressTankDefaultsRef = useRef(false);

  const [dumpDate, setDumpDate] = useState(toDateInputValue(new Date().toISOString()));
  const [form, setForm] = useState({
    invoice: "",
    vehicle: "",
    mobile: "",
    beforeDip: "",
    beforeStock: "",
    receipt: "",
  });

  const [showNozzleModal, setShowNozzleModal] = useState(false);
  const [nozzleReadings, setNozzleReadings] = useState<NozzleReading[]>([]);
  const [nozzleLoading, setNozzleLoading] = useState(false);
  const [customTotalSale, setCustomTotalSale] = useState("");
  const prevTankTabRef = useRef("");

  const parseVal = (val: any) => {
    if (val === undefined || val === null || val === "") return 0;
    const parsed = Number(val);
    return isNaN(parsed) ? 0 : parsed;
  };

  const stockVal = parseVal(form.beforeStock);
  const receiptVal = parseVal(form.receipt);
  const beforeDipVal = parseVal(form.beforeDip);

  const calculatedGross = stockVal + receiptVal;

  const nozzleMeterTotal = useMemo(
    () =>
      nozzleReadings.reduce(
        (sum, nz) => sum + Math.max(0, parseVal(nz.close) - parseVal(nz.open)),
        0
      ),
    [nozzleReadings]
  );

  /** Total Sale: only from nozzle open/close readings — not from Before Dip / Before Stock */
  const displayTotalSale =
    customTotalSale !== ""
      ? (() => {
          const n = Number(customTotalSale);
          return Number.isNaN(n) ? null : Math.max(0, n);
        })()
      : null;

  const resolveTotalSale = (): number | null => {
    if (customTotalSale === "") return null;
    const n = Number(customTotalSale);
    return Number.isNaN(n) ? null : Math.max(0, n);
  };

  // Live: show meter total in Total Sale while entering open/close in nozzle popup
  useEffect(() => {
    if (!showNozzleModal) return;
    const hasClose = nozzleReadings.some((nz) => nz.close !== "");
    if (!hasClose) return;
    setCustomTotalSale(nozzleMeterTotal > 0 ? nozzleMeterTotal.toFixed(2) : "");
  }, [showNozzleModal, nozzleReadings, nozzleMeterTotal]);

  const lastClosingForTank = (tankId: string): number | null => {
    const matching = rows.filter((r) => {
      if (r.tank === tankId) return true;
      const tabTank = tanks.find((t) => t.id === tankId);
      return tabTank ? tankMatchesValue(tabTank, r.tank || "") : false;
    });
    if (matching.length === 0) return null;
    const sorted = [...matching].sort((a, b) => {
      const ta = new Date(a.date || a.endDate || 0).getTime();
      const tb = new Date(b.date || b.endDate || 0).getTime();
      return tb - ta;
    });
    return rowClosingBalance(sorted[0]);
  };

  const lastBalanceHint = activeTab ? lastClosingForTank(activeTab) : null;

  const applyTankDefaults = (tankId: string) => {
    const fromLast = lastClosingForTank(tankId);
    const tank = tanks.find((t) => t.id === tankId);
    const fromTank = tank?.current_stock ?? tank?.currentStock;
    const opening =
      fromLast != null && !Number.isNaN(fromLast)
        ? fromLast
        : fromTank != null && !Number.isNaN(Number(fromTank))
          ? Number(fromTank)
          : null;

    setForm((prev) => ({
      ...prev,
      beforeDip: opening != null ? String(opening) : "",
      beforeStock: opening != null ? String(opening) : "",
      receipt: "",
    }));
    setCustomTotalSale("");
  };

  useEffect(() => {
    if (!activeTab || editingId || suppressTankDefaultsRef.current) return;
    if (prevTankTabRef.current === activeTab) return;
    prevTankTabRef.current = activeTab;
    applyTankDefaults(activeTab);
  }, [activeTab, tanks, editingId]);

  const loadRows = async () => {
    try {
      const res = await fetchJSON("/api/tanker-sales");
      const data = await res.json();
      if (data.ok) {
        setRows((data.rows || []).map((r: Record<string, unknown>) => normalizeTankerRow(r)));
      }
    } catch {
      // ignore — table stays empty
    }
  };

  useEffect(() => {
    loadRows();
  }, []);

  // Prefill from last saved entry once tanker history has loaded
  const rowsBootstrappedRef = useRef(false);
  useEffect(() => {
    if (!activeTab || rows.length === 0 || rowsBootstrappedRef.current) return;
    rowsBootstrappedRef.current = true;
    applyTankDefaults(activeTab);
    prevTankTabRef.current = activeTab;
  }, [rows, activeTab]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetchJSON("/api/tanks-list");
        const data = await res.json();
        if (!data.ok) return;
        const list: TankOption[] = data.rows || [];
        setTanks(list);
        if (list.length > 0) {
          setActiveTab((prev) => (prev && list.some((t) => t.id === prev) ? prev : list[0].id));
        }
      } catch {
        // ignore
      }
    })();
  }, []);

  const loadRowIntoForm = (row: TankerRow) => {
    const recordId = getRowId(row);
    if (!recordId) {
      alert("Cannot edit this row — missing record id.");
      return;
    }

    suppressTankDefaultsRef.current = true;
    const tankId = resolveTankId(tanks, row.tank || "");

    setEditingId(recordId);
    setDumpDate(toDateInputValue(row.date || row.endDate));
    setForm({
      invoice: row.invoice || "",
      vehicle: row.vehicle || "",
      mobile: row.mobile || "",
      beforeDip: row.beforeDip != null ? String(row.beforeDip) : "",
      beforeStock: row.beforeStock != null ? String(row.beforeStock) : "",
      receipt: row.receipt != null ? String(row.receipt) : "",
    });
    const sale = Number(row.totalSale) || 0;
    setCustomTotalSale(sale > 0 ? String(sale) : "");

    if (tankId) {
      prevTankTabRef.current = tankId;
      setActiveTab(tankId);
    }

    window.setTimeout(() => {
      suppressTankDefaultsRef.current = false;
      const el = formSectionRef.current || document.getElementById("tanker-sale-form");
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };

  useEffect(() => {
    if (!pendingEditRowRef.current || tanks.length === 0) return;
    const row = pendingEditRowRef.current;
    pendingEditRowRef.current = null;
    loadRowIntoForm(row);
  }, [tanks]);

  const openNozzleModal = async () => {
    if (!activeTab) {
      alert("Please select a tank first");
      return;
    }
    setShowNozzleModal(true);
    setNozzleLoading(true);
    try {
      const [nozzleRes, readingRes] = await Promise.all([
        fetchJSON(`/api/nozzles-list?tank_id=${encodeURIComponent(activeTab)}`),
        fetchJSON(`/api/nozzles-with-last-readings?date=${encodeURIComponent(dumpDate || new Date().toISOString().slice(0, 10))}`),
      ]);
      const nozzleJson = await nozzleRes.json();
      const readingJson = await readingRes.json();
      const tankNozzles: any[] = nozzleJson.ok ? nozzleJson.rows || [] : [];
      const withReadings: any[] = readingJson.ok ? readingJson.rows || [] : [];

      const merged = (tankNozzles.length > 0 ? tankNozzles : withReadings.filter(
        (n) => String(n.tank_id || n.tankId) === activeTab
      )).map((n: any) => {
        const id = String(n.id || n._id);
        const reading = withReadings.find((r: any) => String(r.id || r._id) === id);
        const open =
          reading?.last_closing_reading ??
          n.last_closing_reading ??
          n.opening_reading ??
          "0";
        const label =
          [n.pump_station, n.nozzle_number].filter(Boolean).join(" · ") ||
          `Nozzle ${n.nozzle_number || id.slice(-4)}`;
        return { id, label, open: String(open), close: "" };
      });

      setNozzleReadings(merged);
    } catch {
      setNozzleReadings([]);
    } finally {
      setNozzleLoading(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setDumpDate(toDateInputValue(new Date().toISOString()));
    setForm({
      invoice: "",
      vehicle: "",
      mobile: "",
      beforeDip: "",
      beforeStock: "",
      receipt: "",
    });
    setNozzleReadings([]);
    setCustomTotalSale("");
    setEditingId(null);
  };

  const handleEdit = (row: TankerRow) => {
    loadRowIntoForm(row);
  };

  const handleSave = async () => {
    if (!activeTab) {
      alert("Please select a tank");
      return;
    }
    if (!dumpDate) {
      alert("Please enter Date");
      return;
    }
    if (!form.beforeStock && !form.receipt) {
      alert("Enter Before Stock and/or Receipt");
      return;
    }
    const saleToSave = resolveTotalSale();
    if (saleToSave == null || saleToSave <= 0) {
      alert("Enter nozzle open/close readings using the orange button, then click Ok");
      return;
    }
    if (saleToSave > calculatedGross) {
      alert("Tanker sale cannot be greater than gross stock");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        date: new Date(`${dumpDate}T12:00:00`).toISOString(),
        invoice: form.invoice.trim(),
        vehicle: form.vehicle.trim(),
        mobile: form.mobile.trim(),
        tank: activeTab,
        beforeDip: beforeDipVal,
        beforeStock: stockVal,
        receipt: receiptVal,
        gross: calculatedGross,
        totalSale: saleToSave,
      };

      const res = await fetchJSON(
        editingId ? `/api/tanker-sales/${editingId}` : "/api/tanker-sales",
        {
          method: editingId ? "PUT" : "POST",
          body: JSON.stringify(payload),
        }
      );
      const data = await res.json();
      if (data.ok) {
        const saved = normalizeTankerRow(data.data || {});
        if (editingId) {
          setRows((prev) => prev.map((r) => (r._id === editingId ? saved : r)));
        } else {
          setRows((prev) => [saved, ...prev]);
        }
        resetForm();
        if (!editingId) applyTankDefaults(activeTab);
      } else {
        alert(data.error || "Failed to save tanker entry");
      }
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : "Failed to save tanker entry");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this tanker entry?")) return;
    try {
      const res = await fetchJSON(`/api/tanker-sales/${id}`, { method: "DELETE" });
      const data = await res.json();
      if (data.ok) {
        setRows((prev) => prev.filter((r) => r._id !== id));
        if (editingId === id) resetForm();
        else applyTankDefaults(activeTab);
      } else {
        alert(data.error || "Delete failed");
      }
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : "Delete failed");
    }
  };

  const tabFiltered = activeTab
    ? rows.filter((r) => {
        if (r.tank === activeTab) return true;
        const tabTank = tanks.find((t) => t.id === activeTab);
        return tabTank ? tankMatchesValue(tabTank, r.tank || "") : false;
      })
    : rows;

  const searched = tabFiltered.filter((r) => {
    const q = search.toLowerCase();
    if (!q) return true;
    return (
      (r.invoice || "").toLowerCase().includes(q) ||
      (r.vehicle || "").toLowerCase().includes(q) ||
      formatDumpDate(r).toLowerCase().includes(q) ||
      resolveTankLabel(tanks, r.tank || "").toLowerCase().includes(q)
    );
  });

  const visibleRows =
    entries === "All" ? searched : searched.slice(0, Number(entries) || searched.length);

  const variation = (row: TankerRow) => {
    const gross = Number(row.gross) || 0;
    const sale = Number(row.totalSale) || 0;
    const dip = Number(row.beforeDip) || 0;
    return (dip - (gross - sale)).toFixed(3);
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 px-6 py-4">
        <nav className="text-sm text-white" aria-label="Breadcrumb">
          <ol className="flex items-center space-x-2">
            <li><span className="text-blue-200">Dashboard</span></li>
            <li className="flex items-center">
              <span className="mx-2 text-blue-300">&gt;</span>
              <span className="text-white font-medium">Add Tanker Details</span>
            </li>
          </ol>
        </nav>
        <div className="mt-2">
          <button type="button" className="bg-blue-800 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium">
            Tanker Details
          </button>
        </div>
      </div>

      <div className="p-6">
        <div
          id="tanker-sale-form"
          ref={formSectionRef}
          className="bg-blue-900 p-6 rounded-lg mb-6 scroll-mt-4"
        >
          {editingId && (
            <p className="text-amber-200 text-sm font-medium mb-3">
              Editing entry — update fields below and click UPDATE
            </p>
          )}
          <div className="grid grid-cols-4 gap-4 mb-4">
            <div className="flex items-center bg-white rounded overflow-hidden h-10 border border-gray-300">
              <span className="bg-[#fbbf24] text-black h-full px-4 flex items-center font-bold text-sm w-32 justify-center select-none">Date</span>
              <input
                type="date"
                value={dumpDate}
                onChange={(e) => setDumpDate(e.target.value)}
                className="w-full h-full px-3 text-black bg-white focus:outline-none text-sm"
              />
            </div>
            <div className="flex items-center bg-white rounded overflow-hidden h-10 border border-gray-300">
              <span className="bg-[#fbbf24] text-black h-full px-4 flex items-center font-bold text-sm w-32 justify-center select-none font-sans">Invoice No</span>
              <input
                type="text"
                value={form.invoice}
                onChange={(e) => handleChange("invoice", e.target.value)}
                className="w-full h-full px-3 text-black bg-white focus:outline-none text-sm"
              />
            </div>
            <div className="flex items-center bg-white rounded overflow-hidden h-10 border border-gray-300">
              <span className="bg-[#fbbf24] text-black h-full px-4 flex items-center font-bold text-sm w-32 justify-center select-none">Vehicle No</span>
              <input
                type="text"
                value={form.vehicle}
                onChange={(e) => handleChange("vehicle", e.target.value)}
                className="w-full h-full px-3 text-black bg-white focus:outline-none text-sm"
              />
            </div>
            <div className="flex items-center bg-white rounded overflow-hidden h-10 border border-gray-300">
              <span className="bg-[#fbbf24] text-black h-full px-4 flex items-center font-bold text-sm w-32 justify-center select-none font-sans">Mobile No</span>
              <input
                type="text"
                value={form.mobile}
                onChange={(e) => handleChange("mobile", e.target.value)}
                className="w-full h-full px-3 text-black bg-white focus:outline-none text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-6 gap-4">
            <div>
              <label className="text-white text-sm font-medium mb-2 block">Tank</label>
              <select
                value={activeTab}
                onChange={(e) => { setActiveTab(e.target.value); setSearch(""); }}
                className="w-full p-2 bg-white border border-gray-300 rounded focus:border-blue-500 focus:outline-none"
              >
                <option value="">- Select Tank-</option>
                {tanks.map((t) => (
                  <option key={t.id} value={t.id}>
                    {getTankLabel(t)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-white text-sm font-medium mb-2 block">Before Dip</label>
              <input
                type="number"
                step="0.01"
                value={form.beforeDip}
                onChange={(e) => handleChange("beforeDip", e.target.value)}
                className="w-full p-2 bg-white border border-gray-300 rounded focus:border-blue-500 focus:outline-none"
                placeholder="Opening dip"
              />
              {lastBalanceHint != null && (
                <p className="text-[11px] text-blue-200 mt-1">
                  Suggested from last entry: <span className="font-semibold text-white">{lastBalanceHint.toFixed(2)} L</span>
                </p>
              )}
            </div>
            <div>
              <label className="text-white text-sm font-medium mb-2 block">Before Stock</label>
              <input
                type="number"
                step="0.01"
                value={form.beforeStock}
                onChange={(e) => handleChange("beforeStock", e.target.value)}
                className="w-full p-2 bg-white border border-gray-300 rounded focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="text-white text-sm font-medium mb-2 block">Receipt</label>
              <input
                type="number"
                value={form.receipt}
                onChange={(e) => handleChange("receipt", e.target.value)}
                className="w-full p-2 bg-white border border-gray-300 rounded focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="text-white text-sm font-medium mb-2 block">Gross Stock</label>
              <input
                type="text"
                readOnly
                value={
                  form.beforeStock !== "" || form.receipt !== ""
                    ? calculatedGross.toFixed(2)
                    : ""
                }
                className="w-full p-2 bg-gray-100 border border-gray-300 text-black rounded font-medium focus:outline-none"
              />
            </div>
            <div className="flex items-end">
              <div className="flex items-center gap-2 w-full">
                <button
                  type="button"
                  onClick={openNozzleModal}
                  title="Open Nozzle Readings"
                  className="w-8 h-8 bg-orange-500 hover:bg-orange-600 active:scale-95 transition-transform rounded-full flex items-center justify-center shrink-0 shadow-md cursor-pointer focus:outline-none"
                >
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </button>
                <div className="flex-1">
                  <label className="text-white text-sm font-medium mb-2 block">Total Sale (Lts.)</label>
                  <input
                    type="text"
                    readOnly
                    value={displayTotalSale != null ? displayTotalSale.toFixed(2) : ""}
                    placeholder="From nozzle readings"
                    className="w-full p-2 bg-white border border-gray-300 text-black rounded font-medium focus:outline-none"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-center mt-6">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="bg-[#84cc16] hover:bg-[#65a30d] disabled:opacity-60 text-white px-12 py-3 text-lg font-medium rounded-lg transition-transform active:scale-95 shadow-md"
            >
              {saving ? "SAVING…" : editingId ? "UPDATE" : "SAVE"}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={resetForm}
                className="ml-4 bg-gray-500 hover:bg-gray-600 text-white px-8 py-3 text-lg font-medium rounded-lg"
              >
                CANCEL
              </button>
            )}
          </div>
        </div>

        <div className="bg-white border rounded-lg">
          <div className="flex border-b overflow-x-auto">
            {tanks.length === 0 ? (
              <span className="px-6 py-3 text-sm text-gray-500">No tanks — add in Tanks &amp; Nozzles</span>
            ) : (
              tanks.map((t) => {
                const label = getTankLabel(t);
                const isActive = activeTab === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => { setActiveTab(t.id); setSearch(""); }}
                    className={`px-6 py-3 font-medium whitespace-nowrap ${
                      isActive ? "bg-blue-900 text-white" : "bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {label}
                  </button>
                );
              })
            )}
          </div>

          <div className="p-4 border-b bg-gray-50">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Show</span>
                <select
                  value={entries}
                  onChange={(e) => setEntries(e.target.value)}
                  className="border border-gray-300 rounded px-3 py-1 w-32 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="All">All</option>
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                <span className="text-sm font-medium">entries</span>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium">Search:</label>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search records..."
                  className="border border-gray-300 rounded px-3 py-1 w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  {["S.No", "Dump Date", "Tank", "Before Dip", "Before Stock", "Receipt", "Gross Stock", "Tanker Sale", "Variation", "Balance", "Action"].map((col) => (
                    <th key={col} className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                      <div className="flex items-center gap-1">
                        {col}
                        <SortIcon />
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleRows.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="px-4 py-8 text-center text-gray-500">
                      No data available in table
                    </td>
                  </tr>
                ) : (
                  visibleRows.map((r, i) => {
                    const rowId = getRowId(r);
                    return (
                    <tr key={rowId || `${r.tank || "tanker"}-${i}`} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">{i + 1}</td>
                      <td className="px-4 py-3 text-sm">{formatDumpDate(r)}</td>
                      <td className="px-4 py-3 text-sm font-medium">{resolveTankLabel(tanks, r.tank || "")}</td>
                      <td className="px-4 py-3 text-sm">{r.beforeDip ?? 0}</td>
                      <td className="px-4 py-3 text-sm">{r.beforeStock ?? 0}</td>
                      <td className="px-4 py-3 text-sm">{r.receipt ?? 0}</td>
                      <td className="px-4 py-3 text-sm">{r.gross ?? 0}</td>
                      <td className="px-4 py-3 text-sm font-semibold">
                        {r.totalSale != null && Number(r.totalSale) > 0 ? Number(r.totalSale).toFixed(3) : "—"}
                      </td>
                      <td className="px-4 py-3 text-sm">{variation(r)}</td>
                      <td className="px-4 py-3 text-sm">
                        {r.totalSale != null && Number(r.totalSale) > 0
                          ? (Number(r.gross) - Number(r.totalSale)).toFixed(3)
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleEdit(r);
                            }}
                            disabled={!rowId}
                            title="Edit"
                            aria-label="Edit tanker entry"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-blue-200 bg-blue-50 text-blue-700 shadow-sm hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (rowId) handleDelete(rowId);
                            }}
                            disabled={!rowId}
                            title="Delete"
                            aria-label="Delete tanker entry"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-red-200 bg-red-50 text-red-700 shadow-sm hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between p-4 border-t bg-gray-50">
            <div className="text-sm text-gray-700">
              Showing {visibleRows.length > 0 ? 1 : 0} to {visibleRows.length} of {searched.length} entries
            </div>
          </div>
        </div>
      </div>

      {/* Nozzle Modal Popup */}
      {showNozzleModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden border border-slate-200">
            {/* Header */}
            <div className="bg-blue-600 text-white px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-bold">Nozzels</h3>
              <button
                type="button"
                onClick={() => setShowNozzleModal(false)}
                className="text-white hover:text-blue-100 transition-colors focus:outline-none"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content Container */}
            <div className="bg-blue-900 p-6 flex flex-col items-center max-h-[70vh] overflow-y-auto w-full">
              {nozzleLoading ? (
                <p className="text-white text-sm py-8">Loading nozzles for this tank…</p>
              ) : nozzleReadings.length === 0 ? (
                <p className="text-blue-100 text-sm py-8 text-center">
                  No nozzles found for this tank. Add nozzles under Master → Tank &amp; Nozzle.
                </p>
              ) : (
                <div className="w-full space-y-4">
                  {nozzleReadings.map((nz, idx) => {
                    const sale = Math.max(0, parseVal(nz.close) - parseVal(nz.open));
                    return (
                      <div
                        key={nz.id}
                        className="bg-blue-800/80 border border-blue-700/50 p-4 rounded-xl flex flex-wrap items-end gap-3 shadow-inner"
                      >
                        <div className="text-white font-bold text-sm min-w-[100px]">{nz.label}</div>
                        <div className="flex-1 min-w-[90px]">
                          <label className="text-white text-[11px] font-bold mb-1 block">Open Reading</label>
                          <input
                            type="number"
                            step="0.01"
                            value={nz.open}
                            onChange={(e) => {
                              const v = e.target.value;
                              setNozzleReadings((prev) =>
                                prev.map((n, i) => (i === idx ? { ...n, open: v } : n))
                              );
                            }}
                            className="w-full p-2 bg-white border border-gray-300 text-black rounded text-sm"
                          />
                        </div>
                        <div className="flex-1 min-w-[90px]">
                          <label className="text-white text-[11px] font-bold mb-1 block">Close Reading</label>
                          <input
                            type="number"
                            step="0.01"
                            value={nz.close}
                            onChange={(e) => {
                              const v = e.target.value;
                              setNozzleReadings((prev) =>
                                prev.map((n, i) => (i === idx ? { ...n, close: v } : n))
                              );
                            }}
                            className="w-full p-2 bg-white border border-gray-300 text-black rounded text-sm"
                            placeholder="Close"
                            autoFocus={idx === 0}
                          />
                        </div>
                        <div className="min-w-[72px]">
                          <label className="text-white text-[11px] font-bold mb-1 block">Sale</label>
                          <div className="p-2 bg-white rounded text-black font-bold text-sm text-center">
                            {nz.close ? sale.toFixed(2) : "—"}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <p className="text-blue-200 text-xs text-center">
                    Total meter sale:{" "}
                    <span className="font-bold text-white">
                      {nozzleReadings
                        .reduce((sum, nz) => sum + Math.max(0, parseVal(nz.close) - parseVal(nz.open)), 0)
                        .toFixed(3)}{" "}
                      L
                    </span>
                  </p>
                </div>
              )}

              {/* Action Button */}
              <button
                type="button"
                disabled={nozzleReadings.length === 0}
                onClick={() => {
                  const total = nozzleReadings.reduce(
                    (sum, nz) => sum + Math.max(0, parseVal(nz.close) - parseVal(nz.open)),
                    0
                  );
                  const saleStr = total > 0 ? total.toFixed(2) : "";
                  setCustomTotalSale(saleStr);
                  setShowNozzleModal(false);
                }}
                className="mt-6 bg-[#f97316] hover:bg-[#ea580c] text-white px-10 py-2 text-sm font-bold rounded-lg transition-transform active:scale-95 shadow-md flex items-center gap-2 focus:outline-none"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
                Ok
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

